import {useState, useCallback, useMemo, useEffect, useRef, type RefObject} from "react";

const PAGE_SIZE = 100;

type UsePaginationResult = {
    currentPage: number;
    totalPages: number;
    rangeStart: number;
    rangeEnd: number;
    pageItems: any[];
    reachedEnd: boolean;
    tableAreaRef: RefObject<HTMLDivElement>;
    handlePageChange: (page: number) => void;
};

export function usePagination(
    items: any[],
    hasMore: boolean,
    loadingMore: boolean,
    loadMoreError: string | null,
    loadMore: () => void,
    filterDeps: any[],
): UsePaginationResult {
    const [currentPage, setCurrentPage] = useState(1);
    const [reachedEnd, setReachedEnd] = useState(false);
    const tableAreaRef = useRef<HTMLDivElement>(null);
    const pendingPageRef = useRef<number | null>(null);

    useEffect(() => {
        setCurrentPage(1);
        setReachedEnd(false);
        pendingPageRef.current = null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, filterDeps);

    const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
    const rangeStart = items.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
    const rangeEnd = Math.min(currentPage * PAGE_SIZE, items.length);

    const pageItems = useMemo(
        () => items.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
        [items, currentPage],
    );

    const handlePageChange = useCallback((page: number) => {
        if (page < 1) return;
        const maxCurrentPage = Math.ceil(items.length / PAGE_SIZE);

        if (page > maxCurrentPage) {
            if (hasMore) {
                pendingPageRef.current = page;
                loadMore();
            } else {
                setReachedEnd(true);
                setTimeout(() => setReachedEnd(false), 2000);
            }
            return;
        }

        pendingPageRef.current = null;
        setCurrentPage(page);
        tableAreaRef.current?.scrollTo({top: 0});
    }, [items.length, hasMore, loadMore]);

    useEffect(() => {
        if (pendingPageRef.current === null || loadingMore) return;

        if (loadMoreError) return;

        const maxPage = Math.ceil(items.length / PAGE_SIZE);
        setCurrentPage(Math.min(pendingPageRef.current, maxPage));
        pendingPageRef.current = null;
        tableAreaRef.current?.scrollTo({top: 0});
    }, [loadingMore, loadMoreError, items.length]);

    return {currentPage, totalPages, rangeStart, rangeEnd, pageItems, reachedEnd, tableAreaRef, handlePageChange};
}
